<template>
  <div class="card">
    <div class="card-header">
      <span class="font-weight-bold">{{ $t("activityAttendance.title") }}</span>
    </div>

    <div class="card-body">
      <a-alert
        v-if="yearLock && !yearLock.isCurrent"
        :type="yearLock.unlocked ? 'warning' : 'info'"
        show-icon
        class="mb-3"
      >
        <div
          slot="message"
          class="d-flex flex-wrap align-items-center justify-content-between"
          style="gap: 12px"
        >
          <span>{{
            yearLock.unlocked
              ? $t("activityAttendance.yearUnlockedForTeachers")
              : $t("activityAttendance.yearLockedForTeachers")
          }}</span>
          <span class="d-flex align-items-center" style="gap: 8px">
            <a-switch
              :checked="yearLock.unlocked"
              :loading="savingYearLock"
              @change="toggleYearLock"
            />
            {{ $t("activityAttendance.allowTeacherEdit") }}
          </span>
        </div>
      </a-alert>

      <a-tabs v-model="activeTab" @change="onTabChange">
        <a-tab-pane key="sessions" :tab="$t('activityAttendance.sessionsTab')">
          <div class="d-flex flex-wrap align-items-end mb-3" style="gap: 12px">
            <div>
              <label class="d-block font-weight-bold">{{
                $t("accueil.activite")
              }}</label>
              <a-select
                v-model="sessionFilters.activity"
                allow-clear
                show-search
                option-filter-prop="children"
                :filter-option="filterOption"
                style="width: 220px"
                :placeholder="$t('accueil.activite')"
                @change="onSessionActivityChange"
              >
                <a-select-option
                  v-for="activity in activities"
                  :key="activity._id"
                  :value="activity._id"
                >
                  {{ activity.designation }}
                </a-select-option>
              </a-select>
            </div>

            <div>
              <label class="d-block font-weight-bold">{{
                $t("activityAttendance.group")
              }}</label>
              <a-select
                v-model="sessionFilters.group"
                allow-clear
                style="width: 180px"
                :placeholder="$t('recette.tous')"
                :disabled="!sessionFilters.activity"
              >
                <a-select-option
                  v-for="group in activityGroups"
                  :key="group.id"
                  :value="group.id"
                >
                  {{ group.name }}
                </a-select-option>
              </a-select>
            </div>

            <div>
              <label class="d-block font-weight-bold">{{
                $t("activityAttendance.period")
              }}</label>
              <a-range-picker
                v-model="sessionDateRange"
                format="DD/MM/YYYY"
                style="width: 260px"
              />
            </div>

            <div>
              <label class="d-block font-weight-bold">{{
                $t("activityAttendance.quickPeriod")
              }}</label>
              <a-select
                v-model="sessionQuickPeriod"
                style="width: 140px"
                @change="applySessionQuickPeriod"
              >
                <a-select-option value="week">{{
                  $t("activityAttendance.weekly")
                }}</a-select-option>
                <a-select-option value="month">{{
                  $t("activityAttendance.monthly")
                }}</a-select-option>
              </a-select>
            </div>

            <a-button
              type="primary"
              :loading="loadingSessions"
              @click="loadSessions"
            >
              {{ $t("action.afficher") }}
            </a-button>
          </div>

          <p class="text-muted mb-3" style="font-size: 12px">
            {{ $t("activityAttendance.sessionsHint") }}
          </p>

          <a-empty
            v-if="!loadingSessions && !filteredSessions.length"
            :description="
              sessionFilters.activity
                ? $t('activityAttendance.noSessions')
                : $t('activityAttendance.pickActivity')
            "
          />

          <a-table
            v-else
            :loading="loadingSessions"
            :data-source="filteredSessions"
            :columns="sessionColumns"
            :pagination="sessionPagination"
            rowKey="id"
            :customRow="sessionRowProps"
            @change="handleSessionTableChange"
          >
            <template slot="day" slot-scope="text">
              {{ formatDay(text) }}
            </template>
            <template slot="time" slot-scope="text, record">
              {{ text }}<span v-if="record.ends">–{{ record.ends }}</span>
            </template>
            <template slot="attendanceStatus" slot-scope="text, record">
              <a-tag v-if="record.canceled" color="orange">{{
                $t("activityAttendance.canceled")
              }}</a-tag>
              <a-tag v-else-if="isFutureSession(record)" color="blue">{{
                $t("activityAttendance.future")
              }}</a-tag>
              <a-tag
                v-else-if="record.attendanceAdminValidated"
                color="green"
                >{{ $t("activityAttendance.adminValidated") }}</a-tag
              >
              <a-tag v-else-if="record.attendanceValidated" color="geekblue">{{
                $t("activityAttendance.pendingAdmin")
              }}</a-tag>
              <a-tag v-else>{{ $t("activityAttendance.notRecorded") }}</a-tag>
              <a-tag
                v-if="!record.canceled && record.attendanceReopened"
                color="gold"
                >{{ $t("activityAttendance.reopened") }}</a-tag
              >
            </template>
            <template slot="teacherValidatedAt" slot-scope="text, record">
              {{
                record.attendanceTeacherValidatedAt
                  ? formatDateTime(record.attendanceTeacherValidatedAt)
                  : "—"
              }}
            </template>
            <template slot="actions" slot-scope="text, record">
              <a-button
                type="link"
                size="small"
                :disabled="record.canceled"
                @click.stop="openSession(record)"
              >
                {{ $t("activityAttendance.openSheet") }}
              </a-button>
              <a-button
                v-if="canReopen(record)"
                type="link"
                size="small"
                @click.stop="confirmReopen(record.id)"
              >
                {{ $t("activityAttendance.reopen") }}
              </a-button>
            </template>
          </a-table>
        </a-tab-pane>

        <a-tab-pane key="summary" :tab="$t('activityAttendance.summaryTab')">
          <div class="d-flex flex-wrap align-items-end mb-3" style="gap: 12px">
            <div>
              <label class="d-block font-weight-bold">{{
                $t("accueil.activite")
              }}</label>
              <a-select
                v-model="filters.activity"
                allow-clear
                show-search
                option-filter-prop="children"
                :filter-option="filterOption"
                style="width: 220px"
                :placeholder="$t('recette.tous')"
              >
                <a-select-option
                  v-for="activity in activities"
                  :key="activity._id"
                  :value="activity._id"
                >
                  {{ activity.designation }}
                </a-select-option>
              </a-select>
            </div>

            <div>
              <label class="d-block font-weight-bold">{{
                $t("sms.listeClasses")
              }}</label>
              <a-select
                v-model="filters.classroom"
                allow-clear
                show-search
                option-filter-prop="children"
                :filter-option="filterOption"
                style="width: 180px"
                :placeholder="$t('recette.tous')"
              >
                <a-select-option
                  v-for="classe in classrooms"
                  :key="classe._id"
                  :value="classe._id"
                >
                  {{ classe.name }}
                </a-select-option>
              </a-select>
            </div>

            <div>
              <label class="d-block font-weight-bold">{{
                $t("activityAttendance.period")
              }}</label>
              <a-range-picker
                v-model="dateRange"
                format="DD/MM/YYYY"
                style="width: 260px"
              />
            </div>

            <div>
              <label class="d-block font-weight-bold">{{
                $t("activityAttendance.quickPeriod")
              }}</label>
              <a-select
                v-model="quickPeriod"
                style="width: 140px"
                @change="applyQuickPeriod"
              >
                <a-select-option value="week">{{
                  $t("activityAttendance.weekly")
                }}</a-select-option>
                <a-select-option value="month">{{
                  $t("activityAttendance.monthly")
                }}</a-select-option>
              </a-select>
            </div>

            <div>
              <label class="d-block font-weight-bold">{{
                $t("activityAttendance.student")
              }}</label>
              <a-input
                v-model="studentSearch"
                allow-clear
                style="width: 180px"
                :placeholder="$t('activityAttendance.student')"
              />
            </div>

            <a-button type="primary" :loading="loading" @click="loadSummary">
              {{ $t("action.afficher") }}
            </a-button>
          </div>

          <p class="text-muted mb-3" style="font-size: 12px">
            {{ $t("activityAttendance.hint") }}
          </p>

          <a-empty
            v-if="!loading && !rows.length"
            :description="$t('activityAttendance.empty')"
          />

          <a-table
            v-else
            :loading="loading"
            :data-source="filteredRows"
            :columns="columns"
            :pagination="pagination"
            rowKey="rowKey"
            @change="handleTableChange"
          />
        </a-tab-pane>
      </a-tabs>
    </div>

    <a-modal
      :visible="sheetVisible"
      :title="$t('activityAttendance.markTitle')"
      :width="860"
      :footer="null"
      :destroyOnClose="true"
      @cancel="closeSheet"
    >
      <div v-if="loadingRoster" class="text-center py-4">
        <a-spin />
      </div>
      <template v-else-if="roster">
        <div class="mb-3">
          <div class="font-weight-bold">
            {{ roster.session.activityName }}
            <template v-if="roster.session.groupName">
              · {{ roster.session.groupName }}
            </template>
          </div>
          <div class="text-muted" style="font-size: 13px">
            {{ formatDay(roster.session.day) }}
            · {{ roster.session.time
            }}<span v-if="roster.session.ends"
              >–{{ roster.session.ends }}</span
            >
            · {{ $t("activityAttendance.teacher") }}:
            {{ roster.session.teacherName || "—" }}
            <template v-if="roster.session.roomName">
              · {{ $t("activityAttendance.room") }}:
              {{ roster.session.roomName }}
            </template>
            ·
            {{
              $t("activityAttendance.studentCount", {
                nb: draftStudents.length,
              })
            }}
          </div>
          <div class="text-muted" style="font-size: 13px">
            {{ $t("activityAttendance.teacherValidatedAt") }}:
            {{
              roster.session.teacherValidatedAt
                ? formatDateTime(roster.session.teacherValidatedAt)
                : $t("activityAttendance.teacherNotValidated")
            }}
          </div>
        </div>

        <a-alert
          v-if="!canMarkRoster"
          type="info"
          show-icon
          class="mb-3"
          :message="$t('activityAttendance.futureHint')"
        />
        <a-alert
          v-else-if="!roster.session.validated"
          type="warning"
          show-icon
          class="mb-3"
          :message="$t('activityAttendance.nothingRecordedYet')"
        />
        <a-alert
          v-else-if="roster.session.adminValidated"
          type="success"
          show-icon
          class="mb-3"
          :message="$t('activityAttendance.validatedHint')"
        />
        <a-alert
          v-else-if="roster.session.reopened"
          type="warning"
          show-icon
          class="mb-3"
          :message="$t('activityAttendance.reopenedHint')"
        />
        <a-alert
          v-else
          type="info"
          show-icon
          class="mb-3"
          :message="$t('activityAttendance.pendingAdminHint')"
        />
        <a-alert
          v-if="roster.session.groupName"
          type="info"
          show-icon
          class="mb-3"
          :message="
            $t('activityAttendance.groupRosterHint', {
              group: roster.session.groupName,
            })
          "
        />

        <a-empty
          v-if="!draftStudents.length"
          :description="$t('activityAttendance.emptyRoster')"
        />

        <a-table
          v-else
          :data-source="draftStudents"
          :columns="sheetColumns"
          :pagination="{ pageSize: 10 }"
          rowKey="id"
          size="middle"
        >
          <template slot="studentName" slot-scope="text, record">
            {{ record.lastName }} {{ record.firstName }}
          </template>
          <template slot="status" slot-scope="text, record">
            <a-radio-group
              :value="record.draftType"
              :disabled="!canMarkRoster || validating"
              @change="(e) => onDraftChange(record, e.target.value)"
            >
              <a-radio value="Present">{{ $t("assiduite.present") }}</a-radio>
              <a-radio value="Retard">{{ $t("assiduite.retard") }}</a-radio>
              <a-radio value="Exclus">{{ $t("assiduite.exclus") }}</a-radio>
              <a-radio value="Absence">{{ $t("assiduite.absence") }}</a-radio>
            </a-radio-group>
          </template>
        </a-table>

        <div
          v-if="draftStudents.length && canMarkRoster"
          class="d-flex justify-content-end mt-3"
          style="gap: 8px"
        >
          <a-button
            v-if="canReopen(roster.session)"
            :loading="reopening"
            @click="confirmReopen(roster.session.id)"
          >
            {{ $t("activityAttendance.reopen") }}
          </a-button>
          <a-button
            type="primary"
            :loading="validating"
            @click="validateAttendance"
          >
            {{ $t("activityAttendance.validate") }}
          </a-button>
        </div>
      </template>
    </a-modal>
  </div>
</template>

<script>
import moment from "moment";
import apiClient from "@/services/axios";
import { showApiError } from "@/utilities/showApiError";

const MARK_TYPES = ["Present", "Retard", "Absence", "Exclus"];

export default {
  name: "ActivityAttendanceSummary",
  data() {
    return {
      activeTab: "sessions",
      loading: false,
      loadingSessions: false,
      loadingRoster: false,
      validating: false,
      reopening: false,
      yearLock: null,
      savingYearLock: false,
      activities: [],
      classrooms: [],
      activityGroups: [],
      sessions: [],
      rows: [],
      filters: {
        activity: undefined,
        classroom: undefined,
      },
      sessionFilters: {
        activity: undefined,
        group: undefined,
      },
      dateRange: [moment().startOf("month"), moment().endOf("month")],
      sessionDateRange: [
        moment().startOf("isoWeek"),
        moment().endOf("isoWeek"),
      ],
      quickPeriod: "month",
      sessionQuickPeriod: "week",
      studentSearch: "",
      pagination: { current: 1, pageSize: 20, total: 0 },
      sessionPagination: { current: 1, pageSize: 15, total: 0 },
      sheetVisible: false,
      selectedSessionId: "",
      roster: null,
      draftStudents: [],
    };
  },
  computed: {
    filteredRows() {
      const q = (this.studentSearch || "").trim().toLowerCase();
      if (!q) return this.rows;
      return this.rows.filter((row) =>
        (row.studentName || "").toLowerCase().includes(q)
      );
    },
    filteredSessions() {
      let list = this.sessions || [];
      if (this.sessionFilters.group) {
        list = list.filter(
          (s) => String(s.activityGroupId || "") === String(this.sessionFilters.group)
        );
      }
      if (this.sessionDateRange && this.sessionDateRange.length === 2) {
        const from = moment(this.sessionDateRange[0]).startOf("day");
        const to = moment(this.sessionDateRange[1]).endOf("day");
        list = list.filter((s) => {
          const day = moment(s.day);
          return !day.isBefore(from) && !day.isAfter(to);
        });
      }
      return list;
    },
    columns() {
      return [
        {
          title: this.$t("activityAttendance.student"),
          dataIndex: "studentName",
          key: "studentName",
        },
        {
          title: this.$t("accueil.activite"),
          dataIndex: "activityName",
          key: "activityName",
        },
        {
          title: this.$t("activityAttendance.expected"),
          dataIndex: "expected",
          key: "expected",
        },
        {
          title: this.$t("activityAttendance.attended"),
          dataIndex: "attended",
          key: "attended",
        },
        {
          title: this.$t("assiduite.absence"),
          dataIndex: "absences",
          key: "absences",
        },
        {
          title: this.$t("assiduite.retard"),
          dataIndex: "delays",
          key: "delays",
        },
        {
          title: this.$t("assiduite.exclus"),
          dataIndex: "exclus",
          key: "exclus",
        },
      ];
    },
    sessionColumns() {
      return [
        {
          title: this.$t("activityAttendance.day"),
          dataIndex: "day",
          key: "day",
          scopedSlots: { customRender: "day" },
        },
        {
          title: this.$t("activityAttendance.time"),
          dataIndex: "time",
          key: "time",
          scopedSlots: { customRender: "time" },
        },
        {
          title: this.$t("activityAttendance.teacher"),
          dataIndex: "teacherName",
          key: "teacherName",
        },
        {
          title: this.$t("activityAttendance.group"),
          dataIndex: "groupName",
          key: "groupName",
        },
        {
          title: this.$t("activityAttendance.room"),
          dataIndex: "roomName",
          key: "roomName",
        },
        {
          title: this.$t("activityAttendance.status"),
          key: "attendanceStatus",
          scopedSlots: { customRender: "attendanceStatus" },
        },
        {
          title: this.$t("activityAttendance.teacherValidatedAt"),
          key: "teacherValidatedAt",
          scopedSlots: { customRender: "teacherValidatedAt" },
        },
        {
          title: this.$t("action.actions"),
          key: "actions",
          scopedSlots: { customRender: "actions" },
        },
      ];
    },
    sheetColumns() {
      return [
        {
          title: this.$t("activityAttendance.student"),
          key: "studentName",
          scopedSlots: { customRender: "studentName" },
        },
        {
          title: this.$t("activityAttendance.status"),
          key: "status",
          scopedSlots: { customRender: "status" },
        },
      ];
    },
    canMarkRoster() {
      if (!this.roster || !this.roster.session) return false;
      if (this.roster.session.canMark === false) return false;
      if (this.isFutureSession(this.roster.session)) return false;
      return true;
    },
  },
  created() {
    this.loadLookups();
    this.loadYearLock();
  },
  methods: {
    filterOption(input, option) {
      return (
        option.componentOptions.children[0].text
          .toLowerCase()
          .indexOf(input.toLowerCase()) >= 0
      );
    },
    formatDay(day) {
      return day ? moment(day).format("DD/MM/YYYY") : "";
    },
    formatDateTime(date) {
      return date ? moment(date).format("DD/MM/YYYY HH:mm") : "";
    },
    isFutureSession(session) {
      if (!session || !session.day) return false;
      return moment
        .utc(session.day)
        .startOf("day")
        .isAfter(moment.utc().startOf("day"));
    },
    sessionRowProps(record) {
      return {
        style: { cursor: record.canceled ? "default" : "pointer" },
        on: {
          click: () => {
            if (!record.canceled) this.openSession(record);
          },
        },
      };
    },
    onTabChange() {},
    applyQuickPeriod(value) {
      if (value === "week") {
        this.dateRange = [
          moment().startOf("isoWeek"),
          moment().endOf("isoWeek"),
        ];
      } else {
        this.dateRange = [moment().startOf("month"), moment().endOf("month")];
      }
      this.loadSummary();
    },
    applySessionQuickPeriod(value) {
      if (value === "week") {
        this.sessionDateRange = [
          moment().startOf("isoWeek"),
          moment().endOf("isoWeek"),
        ];
      } else {
        this.sessionDateRange = [
          moment().startOf("month"),
          moment().endOf("month"),
        ];
      }
    },
    async loadLookups() {
      try {
        const [activitiesRes, classroomsRes] = await Promise.all([
          apiClient.post("/activities/filter", { query: {} }),
          apiClient.post("/classrooms/filter", { query: {} }),
        ]);
        this.activities = activitiesRes.data || [];
        this.classrooms = classroomsRes.data || [];
      } catch (e) {
        showApiError(this, e);
      }
    },
    async onSessionActivityChange(value) {
      this.sessionFilters.activity = value || undefined;
      this.sessionFilters.group = undefined;
      this.activityGroups = [];
      this.sessions = [];
      if (!value) return;
      await Promise.all([this.loadActivityGroups(value), this.loadSessions()]);
    },
    async loadActivityGroups(activityId) {
      if (!activityId) {
        this.activityGroups = [];
        return;
      }
      try {
        const res = await apiClient.get(`/activityGroup/byActivity/${activityId}`);
        this.activityGroups = (res.data.groups || []).map((g) => ({
          id: String(g._id || g.id),
          name: g.name,
        }));
      } catch (e) {
        showApiError(this, e);
        this.activityGroups = [];
      }
    },
    async loadSessions() {
      if (!this.sessionFilters.activity) {
        this.$message.warning(this.$t("activityAttendance.pickActivity"));
        return;
      }
      this.loadingSessions = true;
      try {
        const res = await apiClient.get(
          `/activitySchedules/byActivity/${this.sessionFilters.activity}`
        );
        this.sessions = res.data.sessions || [];
        this.sessionPagination.current = 1;
        this.sessionPagination.total = this.filteredSessions.length;
      } catch (e) {
        showApiError(this, e);
        this.sessions = [];
      } finally {
        this.loadingSessions = false;
      }
    },
    async loadSummary() {
      if (!this.dateRange || this.dateRange.length !== 2) {
        this.$message.warning(this.$t("activityAttendance.needDates"));
        return;
      }
      this.loading = true;
      try {
        const body = {
          from: moment(this.dateRange[0]).format("YYYY-MM-DD"),
          to: moment(this.dateRange[1]).format("YYYY-MM-DD"),
        };
        if (this.filters.activity) body.activity = this.filters.activity;
        if (this.filters.classroom) body.classroom = this.filters.classroom;

        const res = await apiClient.post("/activityAttendance/summary", body);
        this.rows = (res.data.rows || []).map((row) => ({
          ...row,
          rowKey: `${row.studentId}-${row.activityId}`,
        }));
        this.pagination.total = this.filteredRows.length;
        this.pagination.current = 1;
      } catch (e) {
        showApiError(this, e);
        this.rows = [];
      } finally {
        this.loading = false;
      }
    },
    handleTableChange(pagination) {
      this.pagination = { ...this.pagination, ...pagination };
    },
    handleSessionTableChange(pagination) {
      this.sessionPagination = { ...this.sessionPagination, ...pagination };
    },
    buildDraft(data) {
      const validated = Boolean(data.session && data.session.validated);
      this.draftStudents = (data.students || []).map((s) => {
        const type = s.mark && s.mark.type;
        return {
          id: s.id,
          firstName: s.firstName || "",
          lastName: s.lastName || "",
          draftType:
            validated && type && MARK_TYPES.includes(type) ? type : "Present",
        };
      });
    },
    async openSession(record) {
      if (!record || record.canceled) {
        this.$message.info(this.$t("activityAttendance.canceledHint"));
        return;
      }
      this.selectedSessionId = record.id;
      this.sheetVisible = true;
      this.loadingRoster = true;
      this.roster = null;
      this.draftStudents = [];
      try {
        const res = await apiClient.get(
          `/activityAttendance/session/${record.id}`
        );
        this.roster = res.data;
        this.buildDraft(res.data);
      } catch (e) {
        showApiError(this, e);
        this.sheetVisible = false;
      } finally {
        this.loadingRoster = false;
      }
    },
    closeSheet() {
      this.sheetVisible = false;
      this.roster = null;
      this.draftStudents = [];
      this.selectedSessionId = "";
    },
    onDraftChange(student, type) {
      if (!this.canMarkRoster) return;
      student.draftType = type || "Present";
    },
    validateAttendance() {
      if (!this.canMarkRoster || !this.selectedSessionId) return;
      if (!this.draftStudents.length) return;

      const self = this;
      this.$confirm({
        title: this.$t("activityAttendance.validate"),
        content: this.$t("activityAttendance.validateConfirm"),
        okText: this.$t("activityAttendance.validate"),
        cancelText: this.$t("action.annuler"),
        onOk() {
          return self.doValidate();
        },
      });
    },
    async doValidate() {
      this.validating = true;
      try {
        const res = await apiClient.post(
          `/activityAttendance/session/${this.selectedSessionId}/validate`,
          {
            marks: this.draftStudents.map((s) => ({
              student: s.id,
              type: s.draftType || "Present",
            })),
          }
        );
        const idx = this.sessions.findIndex(
          (s) => s.id === this.selectedSessionId
        );
        if (idx >= 0) {
          this.$set(this.sessions, idx, {
            ...this.sessions[idx],
            attendanceValidated: true,
            attendanceAdminValidated: true,
            attendanceReopened: false,
            attendanceValidatedAt:
              (res.data.session && res.data.session.attendanceValidatedAt) ||
              new Date().toISOString(),
          });
        }
        this.$message.success(this.$t("activityAttendance.validateSuccess"));
        this.closeSheet();
      } catch (e) {
        showApiError(this, e);
      } finally {
        this.validating = false;
      }
    },
    canReopen(session) {
      if (!session || session.canceled) return false;
      return Boolean(
        session.adminValidated || session.attendanceAdminValidated
      );
    },
    confirmReopen(sessionId) {
      const self = this;
      this.$confirm({
        title: this.$t("activityAttendance.reopen"),
        content: this.$t("activityAttendance.reopenConfirm"),
        okText: this.$t("activityAttendance.reopen"),
        cancelText: this.$t("action.annuler"),
        onOk() {
          return self.doReopen(sessionId);
        },
      });
    },
    async doReopen(sessionId) {
      this.reopening = true;
      try {
        const res = await apiClient.post(
          `/activityAttendance/session/${sessionId}/reopen`
        );
        const idx = this.sessions.findIndex((s) => s.id === sessionId);
        if (idx >= 0) {
          this.$set(this.sessions, idx, {
            ...this.sessions[idx],
            attendanceAdminValidated: false,
            attendanceReopened: true,
          });
        }
        if (this.roster && this.roster.session.id === sessionId) {
          this.roster = {
            ...this.roster,
            session: { ...this.roster.session, ...res.data.session },
          };
        }
        this.$message.success(this.$t("activityAttendance.reopenSuccess"));
      } catch (e) {
        showApiError(this, e);
      } finally {
        this.reopening = false;
      }
    },
    async loadYearLock() {
      try {
        const res = await apiClient.get("/activityAttendance/yearLock");
        this.yearLock = res.data;
      } catch (e) {
        this.yearLock = null;
        showApiError(this, e);
      }
    },
    async toggleYearLock(unlocked) {
      this.savingYearLock = true;
      try {
        const res = await apiClient.patch("/activityAttendance/yearLock", {
          unlocked,
        });
        this.yearLock = res.data;
        this.$message.success(this.$t("activityAttendance.yearLockUpdated"));
      } catch (e) {
        showApiError(this, e);
      } finally {
        this.savingYearLock = false;
      }
    },
  },
};
</script>
